<script lang="ts">
import { templateRef, useElementSize, useTemplateRefsList } from '@vueuse/core'
import { gsap } from 'gsap'
import { computed, defineComponent, onUnmounted, PropType, watch } from 'vue'

import { useGlobalReducedMotion } from '@/functions/useGlobalReducedMotion'
import { revealCharText } from '@/utils/animations'
export default defineComponent({
  name: 'OverlayTexts',
  props: {
    list: { type: Array as PropType<string[]>, required: true },
    delay: { type: Number, default: 0 }
  },
  setup (props, { attrs, slots, emit }) {
    const wrapper = templateRef<HTMLElement>('wrapper')
    const els = useTemplateRefsList<HTMLElement>()
    const { width: wrapperW, height: wrapperH } = useElementSize(wrapper)
    const reducedMotion = useGlobalReducedMotion()
    const tls = []
    const slotCount = computed(() => props.list.length * 2)
    const slotsY = computed(() => {
      const size = wrapperH.value / slotCount.value
      return new Array(slotCount.value).fill(0)
        .map((_, i) => ({ sort: Math.random(), value: size * i }))
        .sort((a, b) => a.sort - b.sort)
        .map((a) => a.value)
    })
    let current = 0
    const setItemPosition = (item) => {
      const bb = item.getBoundingClientRect()
      current = (current + 1) % slotCount.value
      gsap.set(item, {
        x: (wrapperW.value - bb.width) * gsap.utils.random(0, 1),
        y: slotsY.value[current]
      })
    }

    const animateItem = (item, i) => {
      tls[i]?.kill()
      tls[i] = revealCharText(
        gsap.utils.toArray('.item', item)[0] as HTMLElement,
        {
          delay: props.delay + 1 * i,
          repeat: -1,
          yoyo: true,
          onStart: () => setItemPosition(item),
          onRepeat: function () {
            if (this.progress() < 0.5) setItemPosition(item)
          }
        }
      )
    }

    watch(els, items => items.forEach(animateItem), { deep: true })
    watch([wrapperW, wrapperH], () => els.value.forEach(setItemPosition))

    onUnmounted(() => tls.forEach(tl => tl?.kill()))

    return {
      els,
      reducedMotion
    }
  }
})
</script>

<template>
  <div class="wrapper">
    <div class="relative h-full w-full">
      <div v-if="!reducedMotion" ref="wrapper" class="absolute inset-0 overflow-hidden">
        <div
          v-for="(item, i) in list"
          :key="i"
          :ref="els.set"
          class="absolute max-w-40 lg:max-w-full"
        >
          <span class="inline-block opacity-0 label2 lg:whitespace-nowrap">
            {{ item }}
          </span>
          <span aria-hidden="true" class="item carret absolute top-0 left-0 inline-block label2 c-grey lg:whitespace-nowrap">
            {{ item }}
          </span>
        </div>
      </div>
      <slot />
    </div>
  </div>
</template>
<style lang="scss" scoped>
.wrapper {
  @include ensure-position;
}
</style>
